'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { cn } from '@/shared/utils/cn';
import { Card } from '@/shared/components/ui/card';
import { Loader2, Check, UserRound, Plus, Camera, ChevronDown } from 'lucide-react';
import {
  CharacterSelector,
  characterKeys,
  listActiveCharacterTemplates,
  seedCharactersFromTemplates,
  useCharacters,
} from '@/contexts/characters';
import type { CharacterTemplate, CharacterSelectorHandle, Character } from '@/contexts/characters';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { useCurrentUser } from '@/contexts/authentication/client';
import type { ContentParameters } from './ParametersStep';
import { PhotoHeroCards, PhotoHeroUpload } from './PhotoHeroes';

const INITIAL_VISIBLE = 11; // fill the grid before the "show all" toggle appears

interface HeroesStepProps {
  values: ContentParameters;
  /** an updater preferred: a callback that fires after a load must not spread stale values */
  onChange: (values: ContentParameters | ((prev: ContentParameters) => ContentParameters)) => void;
  locale: string;
  recentCharacterIds?: string[];
  onCharacterNamesChange?: (names: Record<string, string>) => void;
  /** Personal-story intent → open the "from photo" panel by default. */
  autoOpenPhoto?: boolean;
  /** The JOB's own words for this screen — a parent who came for a book about
   *  their child should not be asked "who is in the story?" in the abstract. */
  title?: string;
  subtitle?: string;
}

export function HeroesStep({
  values,
  onChange,
  locale,
  recentCharacterIds,
  onCharacterNamesChange,
  autoOpenPhoto,
  title,
  subtitle,
}: HeroesStepProps) {
  const t = useTranslations('content.wizard.heroes');

  const queryClient = useQueryClient();

  const { user } = useCurrentUser();
  const familyId = user?.familyId ?? undefined;

  const { data: templates, isLoading } = useQuery({
    queryKey: ['character-templates', 'active'],
    queryFn: () => listActiveCharacterTemplates(),
  });
  // Family library characters — merged into the SAME grid as the templates so
  // there's ONE way to pick a ready hero (no separate "library" control, no
  // duplicated recent chips).
  const { data: familyChars = [] } = useCharacters(familyId);

  const [seedingId, setSeedingId] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  // ── Hero from a photo: created AT UPLOAD (pipeline v2) — the record gets its id,
  // consent is recorded and the 3D identity starts drawing; the card shows its state.
  const [photoOpen, setPhotoOpen] = useState(!!autoOpenPhoto);

  // Lets the "Create new" template card drive the library selector's inline
  // creation flow (opens its sheet straight into the character form).
  const selectorRef = useRef<CharacterSelectorHandle>(null);
  const openCreate = useCallback(() => selectorRef.current?.openCreate(), []);

  const toggleTemplate = useCallback(
    async (tpl: CharacterTemplate) => {
      setSeedError(null);

      // Deselect — drop the seeded character from THIS story (the library copy stays).
      if (tpl.seededCharacterId && values.characterIds.includes(tpl.seededCharacterId)) {
        const charId = tpl.seededCharacterId;
        onChange({
          ...values,
          characterIds: values.characterIds.filter((id) => id !== charId),
          characterAssignments: values.characterAssignments.filter(
            (a) => a.characterId !== charId,
          ),
        });
        return;
      }

      // Select — seed (copy-on-first-use, idempotent) then add to the story.
      setSeedingId(tpl.id);
      try {
        const res = await seedCharactersFromTemplates([tpl.id]);
        if (!res.success) {
          setSeedError(res.error);
          return;
        }
        const charId = res.data.characterIds[0] ?? tpl.seededCharacterId;
        if (!charId) return;
        // The seed created a new library character — refresh every character list
        // (CharacterSelector, sidebar library) so it appears immediately.
        void queryClient.invalidateQueries({ queryKey: characterKeys.all });
        if (!values.characterIds.includes(charId)) {
          onChange((prev) => ({ ...prev, characterIds: prev.characterIds.includes(charId) ? prev.characterIds : [...prev.characterIds, charId] }));
        }
      } finally {
        setSeedingId(null);
      }
    },
    [values, onChange, queryClient],
  );

  const toggleChar = useCallback(
    (id: string) => {
      const on = values.characterIds.includes(id);
      onChange({
        ...values,
        characterIds: on ? values.characterIds.filter((x) => x !== id) : [...values.characterIds, id],
        characterAssignments: on
          ? values.characterAssignments.filter((a) => a.characterId !== id)
          : values.characterAssignments,
      });
    },
    [values, onChange],
  );

  // ── ONE unified picker list ──
  // Templates first (the friendly defaults), then the family's own characters.
  // A template that's already been seeded IS a family character — show it once
  // (as the template card) and drop its library twin, so nothing duplicates.
  const photoHeroIds = new Set((values.photoHeroes ?? []).map((h) => h.characterId));
  const seededIds = new Set(
    (templates ?? []).map((t) => t.seededCharacterId).filter(Boolean) as string[],
  );
  type PickerItem =
    | { kind: 'template'; id: string; selId: string | undefined; name: string; img?: string | null; tpl: CharacterTemplate }
    | { kind: 'char'; id: string; selId: string; name: string; img?: string | null; char: Character };
  const items: PickerItem[] = [
    ...(templates ?? []).map((tpl): PickerItem => ({
      kind: 'template',
      id: tpl.id,
      selId: tpl.seededCharacterId,
      name: tpl.name,
      img: tpl.primaryPortraitUrl || tpl.primaryImageUrl,
      tpl,
    })),
    ...familyChars
      // a photo hero of THIS order shows as its own card (with its identity state)
      .filter((c) => !seededIds.has(c.id) && !photoHeroIds.has(c.id))
      .map((c): PickerItem => ({
        kind: 'char',
        id: c.id,
        selId: c.id,
        name: c.name,
        img: c.primaryPortraitUrl || c.primaryImageUrl,
        char: c,
      })),
  ];
  const visible = showAll ? items : items.slice(0, INITIAL_VISIBLE);
  const hiddenCount = items.length - visible.length;

  return (
    <div data-testid="wizard-step-heroes" className="space-y-4 sm:space-y-5">
      <div className="space-y-1">
        <h3 className="text-base font-medium">{title ?? t('title')}</h3>
        <p className="text-sm text-muted-foreground">{subtitle ?? t('subtitle')}</p>
      </div>

      {/* Heroes from a photo: real records with an id; identity drawing / ready / failed. */}
      <PhotoHeroCards values={values} onChange={onChange} language={values.language} />

      {/* ONE unified hero picker — templates + your library, deduped. */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {visible.map((item) => {
              const selected = !!item.selId && values.characterIds.includes(item.selId);
              const seeding = item.kind === 'template' && seedingId === item.id;
              const onPick = () =>
                item.kind === 'template' ? toggleTemplate(item.tpl) : toggleChar(item.selId);
              return (
                <Card
                  key={item.kind === 'template' ? `t_${item.id}` : `c_${item.id}`}
                  data-testid={`heroes-pick-${item.id}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  onClick={onPick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPick();
                    }
                  }}
                  className={cn(
                    'relative flex cursor-pointer flex-col items-center gap-2 p-3 transition-all hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selected && 'border-primary ring-2 ring-primary/20',
                  )}
                >
                  {selected && (
                    <span className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="size-3" />
                    </span>
                  )}
                  <div className="flex size-14 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground">
                    {item.img ? (
                      <Image
                        src={mediaSrc(item.img)}
                        alt={item.name}
                        width={56}
                        height={56}
                        className="size-full object-cover"
                        unoptimized
                      />
                    ) : seeding ? (
                      <Loader2 className="size-5 animate-spin" />
                    ) : (
                      <UserRound className="size-6" />
                    )}
                  </div>
                  <p className="w-full truncate text-center text-sm font-medium">{item.name}</p>
                </Card>
              );
            })}

            {/* Show-all card (only when the list is truncated). */}
            {hiddenCount > 0 && !showAll && (
              <Card
                data-testid="heroes-show-all"
                role="button"
                tabIndex={0}
                onClick={() => setShowAll(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowAll(true);
                  }
                }}
                className="flex cursor-pointer flex-col items-center justify-center gap-2 border-dashed p-3 text-muted-foreground transition-all hover:border-primary/50 hover:text-primary"
              >
                <div className="flex size-14 items-center justify-center rounded-full border-2 border-dashed border-current">
                  <ChevronDown className="size-6" />
                </div>
                <p className="text-sm font-medium">{t('showAll', { count: hiddenCount })}</p>
              </Card>
            )}

            {/* Create-new — opens the (hidden) library selector's inline creator. */}
            <Card
              data-testid="heroes-create-new"
              role="button"
              tabIndex={0}
              onClick={openCreate}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCreate();
                }
              }}
              className="flex cursor-pointer flex-col items-center justify-center gap-2 border-dashed p-3 text-muted-foreground transition-all hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex size-14 items-center justify-center rounded-full border-2 border-dashed border-current">
                <Plus className="size-6" />
              </div>
              <p className="text-sm font-medium">{t('createNew')}</p>
            </Card>

            {/* From-photo — turns a photo into a story hero (hidden pipeline). */}
            <Card
              data-testid="heroes-from-photo"
              role="button"
              tabIndex={0}
              onClick={() => setPhotoOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPhotoOpen((v) => !v);
                }
              }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 border-dashed p-3 text-muted-foreground transition-all hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                photoOpen && 'border-primary text-primary',
              )}
            >
              <div className="flex size-14 items-center justify-center rounded-full border-2 border-dashed border-current">
                <Camera className="size-6" />
              </div>
              <p className="text-sm font-medium">{t('fromPhoto')}</p>
            </Card>
          </div>
        )}
        {seedError && <p className="text-sm text-destructive">{seedError}</p>}

        {/* From-photo inline panel: the pick creates the hero. */}
        {photoOpen && (
          <PhotoHeroUpload
            values={values}
            onChange={onChange}
            language={values.language}
            onDone={() => setPhotoOpen(false)}
            onCancel={() => setPhotoOpen(false)}
          />
        )}
      </div>

      {/* Headless CharacterSelector — its inline list is hidden (the unified grid
          above IS the picker), but it stays mounted to own the create/edit sheet
          (opened via ref from the "Create new" card) and to keep computing the
          role assignments + display names for the selected characters. Its create
          sheet renders in a portal, so hiding the trigger container is fine. */}
      <div className="hidden">
        <CharacterSelector
          ref={selectorRef}
          selectedCharacterIds={values.characterIds}
          onSelectionChange={(ids) => onChange((prev) => ({ ...prev, characterIds: ids }))}
          onAssignmentsChange={(assignments) =>
            onChange((prev) => ({ ...prev, characterAssignments: assignments }))
          }
          onNamesChange={onCharacterNamesChange}
          locale={locale}
          recentCharacterIds={recentCharacterIds}
        />
      </div>

      <p className="text-xs text-muted-foreground">{t('skipHint')}</p>
    </div>
  );
}
